export const GROUPING_SEPARATOR = '.';
export const DECIMAL_SEPARATOR = ',';

const NON_CURRENCY_PATTERN = /[^0-9.,]/g;
const SEPARATOR_PATTERN = /[.,]/g;
const GROUPING_POSITION_PATTERN = /\B(?=(\d{3})+(?!\d))/g;

const isDigit = (char: string | undefined): boolean => char !== undefined && char >= '0' && char <= '9';

const countDigits = (value: string): number => value.split('').filter(isDigit).length;

const countDots = (value: string): number => value.split(GROUPING_SEPARATOR).length - 1;

const joinCurrencyParts = (cleanedInput: string, decimalIndex: number): string => {
    if (decimalIndex < 0) return cleanedInput.replace(SEPARATOR_PATTERN, '');

    const integerPart = cleanedInput.slice(0, decimalIndex).replace(SEPARATOR_PATTERN, '');
    const decimalPart = cleanedInput
        .slice(decimalIndex + 1)
        .replace(SEPARATOR_PATTERN, '')
        .slice(0, 2);

    return `${integerPart}${DECIMAL_SEPARATOR}${decimalPart}`;
};

/**
 * Finds the dot that this edit added. Every dot the previous display already contained is a
 * thousands separator, so only a dot the user just typed can be meant as a decimal separator.
 */
const findAddedDotIndex = (cleanedInput: string, previousValue: string): number => {
    if (countDots(cleanedInput) <= countDots(previousValue)) return -1;

    let index = 0;
    while (index < previousValue.length && cleanedInput[index] === previousValue[index]) {
        index += 1;
    }

    return cleanedInput[index] === GROUPING_SEPARATOR ? index : -1;
};

export const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';

    return value.toLocaleString('de-DE', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });
};

export const filterCurrencyInput = (input: string): string => {
    const cleanedInput = input.replace(NON_CURRENCY_PATTERN, '');
    const commaIndex = cleanedInput.indexOf(DECIMAL_SEPARATOR);
    const dotCount = countDots(cleanedInput);
    const singleDotIndex = dotCount === 1 ? cleanedInput.indexOf(GROUPING_SEPARATOR) : -1;
    const digitsAfterSingleDot = singleDotIndex >= 0 ? cleanedInput.length - singleDotIndex - 1 : 0;
    let decimalIndex = -1;

    if (commaIndex >= 0) {
        decimalIndex = commaIndex;
    } else if (singleDotIndex >= 0 && digitsAfterSingleDot !== 3) {
        decimalIndex = singleDotIndex;
    }

    return joinCurrencyParts(cleanedInput, decimalIndex);
};

/**
 * Filters a single edit of the currently displayed value. The display carries thousands
 * separators, so a dot may only become the decimal separator when the edit added it. Edits that
 * change more than one character (paste, replace) fall back to {@link filterCurrencyInput}.
 */
export const filterCurrencyEdit = (input: string, previousValue: string): string => {
    if (Math.abs(input.length - previousValue.length) !== 1) return filterCurrencyInput(input);

    const cleanedInput = input.replace(NON_CURRENCY_PATTERN, '');
    const commaIndex = cleanedInput.indexOf(DECIMAL_SEPARATOR);
    const decimalIndex = commaIndex >= 0 ? commaIndex : findAddedDotIndex(cleanedInput, previousValue);

    return joinCurrencyParts(cleanedInput, decimalIndex);
};

export const groupCurrencyInput = (filteredInput: string): string => {
    const [integerPart = '', decimalPart] = filteredInput.split(DECIMAL_SEPARATOR);
    const groupedIntegerPart = integerPart.replace(GROUPING_POSITION_PATTERN, GROUPING_SEPARATOR);

    return decimalPart === undefined ? groupedIntegerPart : `${groupedIntegerPart}${DECIMAL_SEPARATOR}${decimalPart}`;
};

/**
 * Maps the caret of the raw input onto the grouped display value by keeping the number of digits
 * to the left of the caret stable, so inserting or removing a thousands separator cannot move it.
 */
export const getCaretPosition = (displayValue: string, rawValue: string, rawCaret: number): number => {
    const valueBeforeCaret = rawValue.slice(0, rawCaret);
    const digitsBeforeCaret = countDigits(valueBeforeCaret);
    let position = digitsBeforeCaret === 0 ? 0 : displayValue.length;
    let seenDigits = 0;

    for (let index = 0; index < displayValue.length && seenDigits < digitsBeforeCaret; index += 1) {
        if (!isDigit(displayValue[index])) continue;

        seenDigits += 1;
        if (seenDigits === digitsBeforeCaret) position = index + 1;
    }

    const lastCharBeforeCaret = valueBeforeCaret.slice(-1);
    const typedSeparator = lastCharBeforeCaret === GROUPING_SEPARATOR || lastCharBeforeCaret === DECIMAL_SEPARATOR;

    return typedSeparator && displayValue[position] === DECIMAL_SEPARATOR ? position + 1 : position;
};

export const parseCurrency = (input: string): number | null => {
    const filteredInput = filterCurrencyInput(input);
    if (filteredInput === '' || filteredInput === DECIMAL_SEPARATOR) return null;

    const numericValue = Number(filteredInput.replace(DECIMAL_SEPARATOR, '.'));
    return Number.isNaN(numericValue) ? null : numericValue;
};


